import { dynamicProductInventoryAction } from '@/src/api/product'
import { useAppSelector } from '@/src/store/hooks'
import { useProfile, useRequisitions } from '@hook'
import { ProductInventoryResMock } from '@type/inventory.type'
import { RequisitionData, RequisitionIncluded, RequisitionItem } from '@type/requisitions.type'
import { FormButton, FormInputField, Modal } from '@ui'
import { getError, showToast } from '@util'
import { Text } from 'grommet'
import { Add, Close, FormDown, FormUp, Subtract } from 'grommet-icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, RegisterOptions, useForm } from 'react-hook-form'

type ApproveMode = 'approve' | 'change_quantity'

type FormValues = {
  approveMode: ApproveMode
} & Record<`quantity_${string}`, number>

type Props = {
  onClose: () => void
  requisitionId?: string
  selectedItems?: RequisitionItem[]
  defaultData?: RequisitionData
  included?: RequisitionIncluded[]
  allPendingItems?: RequisitionItem[]
}

type InventoryAllocation = {
  inventoryId: string
  detailLabel: string
  maxAvailable: number
  allocated: number
}

const approveModeOptions = [
  { label: 'Approve drugs', value: 'approve' },
  { label: 'Change drug quantity', value: 'change_quantity' }
]

const formatInventoryDetail = (inv: ProductInventoryResMock) => {
  const a = inv.attributes
  const drugName = a.drug_name?.trim() || ''
  const bn = a.batch_number?.trim() || '-'
  const strength = a.strength?.trim() || '-'
  const qty = a.stock_quantity ?? '-'
  const unit = a.packaging?.trim() || a.inventory_unit?.trim() || 'tablets'
  const drugPart = drugName ? `Drug: ${drugName} | ` : ''
  return `${drugPart}BN: ${bn} | API Strength: ${strength} | Total: ${qty} ${unit} | Expiry: ${a.expiry_date}`
}

const maxStock = (inv: ProductInventoryResMock) => {
  const q = inv.attributes.stock_quantity ?? inv.attributes.quantity_received
  return typeof q === 'number' && !Number.isNaN(q) ? Math.max(0, q) : 0
}

const allocationQtyInputKey = (itemId: string, inventoryId: string) => `${itemId}::${inventoryId}`

export const ApproveRequisitionDrugs = ({
  onClose,
  requisitionId = '',
  selectedItems = [],
  defaultData: _defaultData,
  included: _included,
  allPendingItems: _allPendingItems
}: Props) => {
  const currentStaffId = useAppSelector(state => state.user.user?.authable_id)
  const { facilityId } = useProfile({ isLoggedIn: !!currentStaffId })
  const facilityIdStr = facilityId ? String(facilityId) : ''

  const normalizedItems = useMemo(
    () => selectedItems.filter(item => item.id !== undefined && item.id !== null),
    [selectedItems]
  )

  const itemIdsKey = useMemo(() => normalizedItems.map(i => String(i.id)).join(','), [normalizedItems])

  const [openInventoryItemId, setOpenInventoryItemId] = useState<string | null>(null)
  const [inventoryRowsByItem, setInventoryRowsByItem] = useState<Record<string, ProductInventoryResMock[]>>({})
  const [inventoryLoading, setInventoryLoading] = useState<Record<string, boolean>>({})
  const [allocationsByItem, setAllocationsByItem] = useState<Record<string, InventoryAllocation[]>>({})
  const [nestedExpanded, setNestedExpanded] = useState<Record<string, boolean>>({})
  const [activeAllocationQtyInput, setActiveAllocationQtyInput] = useState<{ key: string; draft: string } | null>(null)

  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    setAllocationsByItem({})
    setInventoryRowsByItem({})
    setOpenInventoryItemId(null)
    setNestedExpanded({})
    setActiveAllocationQtyInput(null)
  }, [itemIdsKey])

  useEffect(() => {
    if (!openInventoryItemId) return
    const handler = (e: MouseEvent) => {
      const node = dropdownRefs.current[openInventoryItemId]
      if (node && !node.contains(e.target as Node)) {
        setOpenInventoryItemId(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openInventoryItemId])

  const defaultValues = useMemo<FormValues>(() => {
    const values: FormValues = { approveMode: 'change_quantity' } as FormValues
    normalizedItems.forEach(item => {
      values[`quantity_${item.id}`] = Number(item.requested_quantity || item.approved_quantity || 0)
    })
    return values
  }, [normalizedItems])

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const {
    watch,
    handleSubmit,
    register,
    getValues,
    formState: { errors, isSubmitting }
  } = methods

  const { reviewRequisition } = useRequisitions()
  const approveMode = watch('approveMode')

  const getQuantityRules = (): RegisterOptions<FormValues, `quantity_${string}`> => ({
    valueAsNumber: true,
    validate: value => {
      if (approveMode === 'change_quantity') {
        if (isNaN(value) || value === 0) return 'Quantity is required'
        if (value < 1) return 'Quantity must be greater than 0'
      }
      return true
    }
  })

  const loadInventoryForRow = useCallback(
    async (item: RequisitionItem) => {
      const itemId = String(item.id)
      const generic = (item.generic_name || '').trim()
      if (!generic) {
        showToast('Generic name is required to load inventory')
        return
      }
      setInventoryLoading(prev => ({ ...prev, [itemId]: true }))
      try {
        const { data } = await dynamicProductInventoryAction({
          generic_name: generic,
          stock_location: 'pharmacy',
          ...(facilityIdStr ? { facility_id: facilityIdStr, main_pharmacy_only: 'true' } : {})
        })
        const rows = (data || []).filter((inv): inv is ProductInventoryResMock => {
          if (!inv?.id) return false
          if (inv.attributes?.stock_location && inv.attributes.stock_location !== 'pharmacy') {
            return false
          }
          return Number(inv.attributes?.stock_quantity ?? 0) > 0
        })
        setInventoryRowsByItem(prev => ({ ...prev, [itemId]: rows }))
      } catch {
        showToast('Could not load inventory')
      } finally {
        setInventoryLoading(prev => ({ ...prev, [itemId]: false }))
      }
    },
    [facilityIdStr]
  )

  const toggleInventoryCheckbox = useCallback(
    (itemId: string, inv: ProductInventoryResMock, checked: boolean) => {
      const id = String(inv.id)
      const detail = formatInventoryDetail(inv)
      const cap = maxStock(inv)
      setAllocationsByItem(prev => {
        const list = prev[itemId] || []
        if (!checked) {
          return { ...prev, [itemId]: list.filter(a => a.inventoryId !== id) }
        }
        if (list.some(a => a.inventoryId === id)) return prev
        const requested = Number(getValues(`quantity_${itemId}` as `quantity_${string}`)) || 1
        // Default pick is up to approved/requested qty and on-hand; user may edit each
        // line to be less, equal, or higher before submit.
        const allocated = cap > 0 ? Math.min(cap, Math.max(1, requested)) : Math.max(1, requested)
        return {
          ...prev,
          [itemId]: [
            ...list,
            { allocated, detailLabel: detail, inventoryId: id, maxAvailable: Math.max(cap, allocated) }
          ]
        }
      })
      setNestedExpanded(prev => ({ ...prev, [itemId]: true }))
    },
    [getValues]
  )

  const updateAllocationQty = useCallback((itemId: string, inventoryId: string, delta: number) => {
    const k = allocationQtyInputKey(itemId, inventoryId)
    setActiveAllocationQtyInput(prev => (prev?.key === k ? null : prev))
    setAllocationsByItem(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || []).map(row => {
        if (row.inventoryId !== inventoryId) return row
        const next = Math.max(0, row.allocated + delta)
        return { ...row, allocated: next }
      })
    }))
  }, [])

  const setAllocationQty = useCallback((itemId: string, inventoryId: string, raw: number) => {
    if (Number.isNaN(raw)) return
    const k = allocationQtyInputKey(itemId, inventoryId)
    setActiveAllocationQtyInput(prev => (prev?.key === k ? null : prev))
    setAllocationsByItem(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || []).map(row => {
        if (row.inventoryId !== inventoryId) return row
        const next = Math.max(0, Math.floor(raw))
        return { ...row, allocated: next }
      })
    }))
  }, [])

  const removeAllocation = useCallback((itemId: string, inventoryId: string) => {
    const k = allocationQtyInputKey(itemId, inventoryId)
    setActiveAllocationQtyInput(prev => (prev?.key === k ? null : prev))
    setAllocationsByItem(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter(a => a.inventoryId !== inventoryId)
    }))
  }, [])

  const onSubmit = async (data: FormValues) => {
    if (!requisitionId || normalizedItems.length === 0) {
      showToast('No pending requisition selected')
      return
    }

    const itemsPayload = normalizedItems.map(item => {
      const itemId = String(item.id)
      const currentValue = data[`quantity_${itemId}`] || 0
      const originalValue = Number(item.requested_quantity || item.approved_quantity || 0)
      const approvedQty = approveMode === 'change_quantity' ? currentValue : originalValue
      const approvedStr = String(approvedQty)

      const rawAllocations = (allocationsByItem[itemId] || []).filter(a => a.allocated > 0)
      const source_allocations = rawAllocations.map(a => ({
        product_inventory_id: a.inventoryId,
        quantity: String(a.allocated)
      }))

      return {
        approved_quantity: approvedStr,
        id: itemId,
        ...(source_allocations.length > 0 ? { source_allocations } : {}),
        status: 'approved' as const
      }
    })

    // for (const item of itemsPayload) {
    //   if (item.source_allocations?.length) {
    //     const sumAlloc = item.source_allocations.reduce((s, a) => s + Number(a.quantity), 0)
    //     if (sumAlloc !== Number(item.approved_quantity)) {
    //       showToast(
    //         `Batch quantities must equal approved quantity (${item.approved_quantity}). Current sum: ${sumAlloc}`
    //       )
    //       return
    //     }
    //   }
    // }

    if (approveMode === 'change_quantity') {
      const missingAllocations = itemsPayload.some(item => !item.source_allocations?.length)
      if (missingAllocations) {
        showToast('Select at least one inventory batch per line when changing quantity.')
        return
      }
    }

    try {
      await reviewRequisition({
        id: requisitionId,
        payload: {
          data: {
            type: 'requisition',
            attributes: {
              items: itemsPayload
            }
          }
        }
      })

      showToast('Requisition item(s) approved successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Approve Drugs">
        <div className="flex flex-col px-7 py-4 gap-6 max-w-[720px] min-w-[min(560px,94vw)] overflow-auto">
          {/* <Text className="mb-1" size="15px">
            How do you want to proceed with this approval?
          </Text> */}
          {/* <Controller
            control={methods.control}
            name="approveMode"
            render={({ field }) => (
              <div className="flex flex-col gap-2 mb-2">
                {approveModeOptions.map(option => (
                  <div className="flex items-center gap-3" key={option.value}>
                    <RadioBtn
                      {...field}
                      checked={field.value === option.value}
                      id={option.value}
                      name={field.name}
                      onChange={() => field.onChange(option.value)}
                      value={option.value}
                    />
                    <Text size="small">{option.label}</Text>
                  </div>
                ))}
                {errors.approveMode && (
                  <Text color="status-error" size="xsmall">
                    {errors.approveMode.message}
                  </Text>
                )}
              </div>
            )}
            rules={{ required: 'Please select an approval mode' }}
          /> */}
          <div
            className={`flex flex-col gap-0 overflow-auto min-h-[300px] ${approveMode === 'approve' ? 'opacity-30' : 'opacity-100'}`}
          >
            {normalizedItems.map(item => {
              const itemId = String(item.id)
              const fieldName = `quantity_${itemId}` as const
              const fieldError = errors[fieldName]
              const genericName = (item.generic_name || '').trim()
              const quantityDisabled = approveMode !== 'change_quantity'
              const isOpen = openInventoryItemId === itemId
              const rows = inventoryRowsByItem[itemId] || []
              const allocations = allocationsByItem[itemId] || []
              const expanded = nestedExpanded[itemId] ?? allocations.length > 0
              const selectedIds = new Set(allocations.map(a => a.inventoryId))

              return (
                <div className="border-b border-neutral-200 py-3 last:border-b-0" key={itemId}>
                  <div className="grid grid-cols-3 items-center gap-3 relative">
                    <Text className="text-sm font-medium text-[#242426] col-span-1" size="small">
                      {item.generic_name || item.brand_name || `Drug ${itemId}`}
                    </Text>
                    <div className="col-span-2 grid gap-3 grid-cols-4">
                      <div className={`min-w-0 ${quantityDisabled ? 'opacity-40' : ''} col-span-1`}>
                        <FormInputField
                          borderRadius="9999px"
                          className="!min-h-10"
                          disabled={approveMode === 'approve'}
                          inputClassName="!text-center"
                          placeholder="0"
                          rounded="pill"
                          size="md"
                          type="number"
                          {...register(fieldName, getQuantityRules())}
                        />
                        {fieldError && (
                          <Text color="status-error" margin={{ top: '4px' }} size="10px">
                            {fieldError.message as string}
                          </Text>
                        )}
                      </div>
                      <div className="col-span-3 flex flex-1">
                        <div
                          className={`min-w-0 flex-1`}
                          ref={el => {
                            dropdownRefs.current[itemId] = el
                          }}
                        >
                          <button
                            className={`flex h-10 w-full items-center justify-between gap-2 rounded-full border border-black/30 bg-white px-3 text-left text-sm text-[#242426] outline-none transition hover:border-[#A79A46] disabled:cursor-not-allowed disabled:opacity-50 ${approveMode === 'approve' ? 'cursor-not-allowed' : ''}`}
                            disabled={!genericName || approveMode === 'approve'}
                            onClick={async () => {
                              if (!genericName) return
                              if (isOpen) {
                                setOpenInventoryItemId(null)
                                return
                              }
                              setOpenInventoryItemId(itemId)
                              if (!inventoryRowsByItem[itemId]?.length) {
                                await loadInventoryForRow(item)
                              }
                            }}
                            type="button"
                          >
                            <span className="truncate text-neutral-500">Select from inventory</span>
                            <FormDown className="shrink-0 text-[#A79A46]" size="18px" />
                          </button>

                          {isOpen ? (
                            <div className="absolute right-0 top-[calc(100%+4px)] z-[100] max-h-72 w-[min(100vw-3rem,420px)] overflow-y-auto rounded-xl border border-neutral-200 bg-white py-1 shadow-lg sm:left-0 sm:right-auto sm:w-full">
                              {inventoryLoading[itemId] ? (
                                <p className="px-3 py-2 text-center text-sm text-neutral-500">Loading…</p>
                              ) : rows.length === 0 ? (
                                <p className="px-3 py-4 text-center text-sm text-neutral-500">No inventory rows</p>
                              ) : (
                                rows.map(inv => {
                                  const invId = String(inv.id)
                                  const checked = selectedIds.has(invId)
                                  return (
                                    <label
                                      className="flex cursor-pointer items-start gap-3 border-b border-neutral-100 px-3 py-2.5 last:border-b-0 hover:bg-neutral-50"
                                      key={invId}
                                    >
                                      <input
                                        checked={checked}
                                        className="mt-1 h-4 w-4 shrink-0 rounded border-neutral-300 accent-[#A79A46]"
                                        onChange={e => toggleInventoryCheckbox(itemId, inv, e.target.checked)}
                                        type="checkbox"
                                      />
                                      <span className="text-sm leading-snug text-neutral-800">
                                        {formatInventoryDetail(inv)}
                                      </span>
                                    </label>
                                  )
                                })
                              )}
                            </div>
                          ) : null}
                        </div>

                        <button
                          aria-expanded={expanded}
                          aria-label={expanded ? 'Hide batch allocations' : 'Show batch allocations'}
                          className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-full text-[#A79A46] hover:bg-neutral-100 disabled:opacity-30"
                          disabled={allocations.length === 0}
                          onClick={() => setNestedExpanded(prev => ({ ...prev, [itemId]: !expanded }))}
                          type="button"
                        >
                          {expanded ? <FormUp size="18px" /> : <FormDown size="18px" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {expanded && allocations.length > 0 ? (
                    <div className="mt-2 space-y-0 border-t border-neutral-100 pt-2">
                      {allocations.map(row => {
                        const qtyKey = allocationQtyInputKey(itemId, row.inventoryId)
                        const isQtyInputActive = activeAllocationQtyInput?.key === qtyKey
                        const qtyDisplayValue = isQtyInputActive
                          ? activeAllocationQtyInput.draft
                          : String(row.allocated)
                        return (
                          <div
                            className="flex flex-col gap-2 border-b border-neutral-100 py-2.5 pl-2 sm:flex-row sm:items-center sm:justify-between sm:pl-6 last:border-b-0"
                            key={row.inventoryId}
                          >
                            <p className="min-w-0 flex-1 text-xs leading-relaxed text-neutral-700 sm:text-sm">
                              {row.detailLabel}
                            </p>
                            <div className="flex shrink-0 items-center gap-2">
                              <div className="flex h-9 items-center rounded-full border border-black/20 overflow-hidden">
                                <button
                                  className="flex h-6 w-6 items-center justify-center text-[#A79A46] hover:bg-neutral-50 disabled:opacity-40"
                                  disabled={row.allocated <= 0}
                                  onClick={() => updateAllocationQty(itemId, row.inventoryId, -1)}
                                  type="button"
                                >
                                  <Subtract size="12px" />
                                </button>
                                <input
                                  aria-label="Allocated quantity for this batch"
                                  autoComplete="off"
                                  className="min-w-[2.5rem] max-w-[4.5rem] border-0 bg-transparent px-0.5 text-center text-sm font-medium tabular-nums text-[#242426] outline-none focus:ring-0"
                                  inputMode="numeric"
                                  onBlur={e => {
                                    const raw = e.target.value.trim()
                                    let n = 0
                                    if (raw !== '') {
                                      const normalized = raw.replace(/^0+/, '') || '0'
                                      n = Number(normalized)
                                      if (Number.isNaN(n)) n = 0
                                    }
                                    setAllocationQty(itemId, row.inventoryId, n)
                                  }}
                                  onChange={e => {
                                    const digitsOnly = e.target.value.replace(/\D/g, '')
                                    setActiveAllocationQtyInput({ draft: digitsOnly, key: qtyKey })
                                  }}
                                  onFocus={() => {
                                    setActiveAllocationQtyInput({ draft: String(row.allocated), key: qtyKey })
                                  }}
                                  pattern="[0-9]*"
                                  type="text"
                                  value={qtyDisplayValue}
                                />
                                <button
                                  className="flex h-6 w-6 items-center justify-center text-[#A79A46] hover:bg-neutral-50 disabled:opacity-40"
                                  disabled={row.maxAvailable > 0 && row.allocated >= row.maxAvailable}
                                  onClick={() => updateAllocationQty(itemId, row.inventoryId, 1)}
                                  type="button"
                                >
                                  <Add size="12px" />
                                </button>
                              </div>
                              <button
                                aria-label="Remove batch"
                                className="flex h-6 w-6 items-center justify-center rounded-full text-neutral-400 hover:bg-rose-50 hover:text-rose-600"
                                onClick={() => removeAllocation(itemId, row.inventoryId)}
                                type="button"
                              >
                                <Close size="12px" />
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>

        <FormButton
          cancel="Cancel"
          disabled={isSubmitting}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
          onClose={onClose}
          submit="Approve"
          type="button"
        />
      </Modal>
    </FormProvider>
  )
}
